//
// DISCLAIMER
//
// Copyright 2019-2026 ArangoDB GmbH, Cologne, Germany
//
// Author Robert Stam
//

import { DataCluster as ApiDataCluster, DataClusterDedicationMode as ApiDataClusterDedicationMode } from "../../api/lib";

// Row tints for the dedicated-data-cluster addon. A recorded dedicated cluster
// is billable and known (amber); a private cluster with no mode recorded still
// needs attention (red, the tint private clusters have always had).
export const dedicatedBackground = "rgba(255, 165, 0, 0.14)";
export const unrecordedBackground = "rgba(255, 0, 0, 0.1)";

// IDedication is how a data cluster is presented with respect to the
// dedicated-data-cluster addon.
export interface IDedication {
  label: string;
  color: "teal" | "blue" | "red" | "grey";
  popup: string;
  // Row tint for the list. Absent for an ordinary shared cluster, which is the
  // common case and must not colour the whole table.
  background?: string;
}

// The API returns enum values as strings in JSON ("DATA_CLUSTER_DEDICATION_MODE_..."),
// while the generated TS enum is numeric — comparing the raw field against an enum
// member never matches. Same normalisation as normalizeIngestionStatus in
// UsageItemList.tsx. An unreadable value falls back to NONE, which is the safe
// direction: a private cluster then reads as "Not recorded" (not billed) rather than
// silently claiming a mode it does not have.
const normalizeDedicationMode = (mode?: ApiDataClusterDedicationMode | string | null): ApiDataClusterDedicationMode => {
  if (mode == null || mode === "") {
    return ApiDataClusterDedicationMode.DATA_CLUSTER_DEDICATION_MODE_NONE;
  }
  if (typeof mode === "number") {
    return mode;
  }
  const fromEnumName = ApiDataClusterDedicationMode[mode as keyof typeof ApiDataClusterDedicationMode];
  if (typeof fromEnumName === "number") {
    return fromEnumName;
  }
  const numeric = Number(mode);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return ApiDataClusterDedicationMode.DATA_CLUSTER_DEDICATION_MODE_NONE;
};

// dedicationOf returns how to present a cluster's dedication. It always returns a
// value: the column and the detail field are permanent, so a shared cluster reads
// as "Shared" rather than leaving a blank that looks like missing data.
//
// DATA_CLUSTER_DEDICATION_MODE_NONE is ambiguous by design: it means both
// "ordinary shared cluster" and "dedicated cluster created before the field
// existed". A non-empty private_to_organizations tells the two apart, and that
// second case is worth surfacing on its own — the addon is billed off the mode,
// so a dedicated cluster left unrecorded is silently never charged.
export const dedicationOf = (datacluster: ApiDataCluster): IDedication => {
  switch (normalizeDedicationMode(datacluster.dedication_mode)) {
    case ApiDataClusterDedicationMode.DATA_CLUSTER_DEDICATION_MODE_PER_DEPLOYMENT:
      return {
        label: "Per deployment",
        color: "teal",
        popup: "Dedicated to a single deployment. The addon is billed through that deployment's sales order.",
        background: dedicatedBackground,
      };
    case ApiDataClusterDedicationMode.DATA_CLUSTER_DEDICATION_MODE_PER_MULTIPLE_DEPLOYMENTS:
      return {
        label: "Per organization",
        color: "blue",
        popup: "Dedicated to one organization and shared by several of its deployments. The addon is billed through the cluster's own sales order.",
        background: dedicatedBackground,
      };
    default:
      if ((datacluster.private_to_organizations || []).length > 0) {
        return {
          label: "Not recorded",
          color: "red",
          popup:
            "This cluster is private to an organization but has no dedication mode recorded, which happens for clusters created before the field existed. The dedicated-data-cluster addon is NOT billed until the mode is set.",
          background: unrecordedBackground,
        };
      }
      return {
        label: "Shared",
        color: "grey",
        popup: "Not dedicated: an ordinary shared platform cluster. The dedicated-data-cluster addon does not apply.",
      };
  }
};

export class ProvisionInfoStatusResult {
  public warning?: boolean;
  public negative?: boolean;
  public message?: string;
  public allGood?: boolean;
}

export class DataClusterHelper {
  static getProvisionInfoStatus(datacluster: ApiDataCluster): ProvisionInfoStatusResult {
    const result = new ProvisionInfoStatusResult();
    const latest = datacluster.latest_provision_info || {};
    const current = datacluster.target_provision_info || latest;
    const status = datacluster.status || {};
    if (current.provision_info_hash != status.provision_info_hash) {
      result.negative = true;
      result.message = "Updating...";
      return result;
    }
    if (!datacluster.target_provision_info) {
      result.warning = true;
      result.message = "Pass-through";
      return result;
    }
    if (datacluster.target_provision_info.provision_info_hash != latest.provision_info_hash) {
      result.warning = true;
      result.message = "Update available";
      return result;
    }

    result.message = "Up-to-date";
    result.allGood = true;
    return result;
  }
}
